import * as THREE from 'three';
import store from '../../redux/store';
import {
  selected_object,
  setPushIntersectedObject,
} from '../../redux/Slice/gridSlice';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import {
  startAnimation,
  stopAnimation,
  pauseAnimation,
} from './animationControls';
import {
  createParticleTexture,
  applyParticleShapeMap,
  getDefaultParticleShape,
} from './particleShapeTextures';
import { hasValidParticleCount } from '../ThreeJsUtils/assetVisibilityUtils';
import {
  ensurePointsMaterialOrthoSupport,
  resyncSceneParticlePoints,
} from './syncParticlePointSizesForCamera';
import { isParticleLifecycleSuspendedInDesign } from './lifeUtils';

const handleStars = (scene, particle) => {
  if (!scene) return;

  // Use dynamic properties
  const particleCount = particle?.particleCount || 300;
  const radius = particle?.radius || 30;
  const radiusVariation = particle?.radiusVariation || 100;
  const altitude = particle?.altitude || 30;
  const color = particle?.color || '#ffffff';
  const opacity = particle?.opacity || 0.9;
  const size = particle?.size || 5;
  const twinkleSpeed = particle?.twinkleSpeed || 2;
  const twinkleIntensity = particle?.twinkleIntensity || 0.5;
  const nebulaCount = particle?.nebulaCount || 5;
  const shootingStarChance = particle?.shootingStarChance || 0.002;
  const shape = particle?.shape ?? getDefaultParticleShape('stars');
  const shapeRotation = particle?.shapeRotation || 0;
  const shapeScale = particle?.shapeScale || 1.0;

  // Create enhanced star field with nebula effects
  const starGroup = new THREE.Group();

  // Main stars
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(particleCount * 3);
  const colors = new Float32Array(particleCount * 3);
  const sizes = new Float32Array(particleCount);
  // per-particle twinkle phase and rate arrays
  const twinklePhase = new Float32Array(particleCount);
  const twinkleRate = new Float32Array(particleCount);

  for (let i = 0; i < particleCount; i++) {
    // Spherical distribution
    const dynamicRadius = radius + Math.random() * radiusVariation;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(Math.random() * 2 - 1);

    positions[i * 3] = dynamicRadius * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = Math.abs(dynamicRadius * Math.cos(phi)) + altitude;
    positions[i * 3 + 2] = dynamicRadius * Math.sin(phi) * Math.sin(theta);

    // Enhanced color variety
    const starType = Math.random();
    const starColor = new THREE.Color();

    if (starType < 0.6) {
      // White/blue stars
      starColor.setHSL(0.6 + Math.random() * 0.1, 0.3, 0.9);
    } else if (starType < 0.85) {
      // Yellow/orange stars
      starColor.setHSL(0.1 + Math.random() * 0.1, 0.8, 0.8);
    } else {
      // Red giants
      starColor.setHSL(0.0, 0.9, 0.7);
    }

    if (particle?.color) {
      const overrideColor = new THREE.Color(particle.color);
      colors[i * 3] = overrideColor.r;
      colors[i * 3 + 1] = overrideColor.g;
      colors[i * 3 + 2] = overrideColor.b;
    } else {
      colors[i * 3] = starColor.r;
      colors[i * 3 + 1] = starColor.g;
      colors[i * 3 + 2] = starColor.b;
    }

    sizes[i] = Math.random() * 3 + 0.5;
    // random phase between 0..2PI
    twinklePhase[i] = Math.random() * Math.PI * 2;
    // twinkleRate is based on the user-specified twinkleSpeed (with some variation per-particle)
    twinkleRate[i] = twinkleSpeed * (0.5 + Math.random());
  }

  // Store base sizes for animation (needed for size variation during blinking)
  const baseSizesArray = new Float32Array(sizes);

  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  // Keep a copy of the base colors so we can modulate brightness per-frame
  const baseColors = new Float32Array(colors);
  geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
  geometry.setAttribute(
    'twinklePhase',
    new THREE.BufferAttribute(twinklePhase, 1)
  );
  geometry.setAttribute(
    'twinkleRate',
    new THREE.BufferAttribute(twinkleRate, 1)
  );
  // Store base sizes in geometry userData for animation
  geometry.userData.baseSizes = baseSizesArray;
  geometry.userData.baseColors = baseColors;

  const material = new THREE.PointsMaterial({
    size: size,
    vertexColors: true,
    transparent: true,
    opacity: opacity,
    sizeAttenuation: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    alphaTest: 0.01,
    map: createParticleTexture(shape, color, 64, shapeRotation, shapeScale),
  });
  material.userData = material.userData || {};
  material.userData.authoredSize = size;
  ensurePointsMaterialOrthoSupport(material);

  const stars = new THREE.Points(geometry, material);
  starGroup.add(stars);

  starGroup.deleteType = 'particle';
  starGroup.objType = 'stars';
  const uniqueId = `stars_${Date.now()}`;

  // Set the name based on particle data or generate unique ID
  if (particle && particle.id) {
    starGroup.name = particle.id;
  } else if (particle && particle._id) {
    starGroup.name = particle._id;
  } else {
    starGroup.name = uniqueId;
  }

  starGroup.displayName = 'Stars'; // For UI display
  starGroup.url = uniqueId;
  starGroup.asset_file_name = 'stars';
  starGroup.file_name = 'stars';
  starGroup.mesh_code = 'stars';
  starGroup.glb_type = 'shapes';
  starGroup.createdAt = new Date().toISOString();
  starGroup.userData = {
    material_settings: {
      shader: 'Standard',
      albedo: color,
      roughness: 0.0,
      metalness: 0.0,
    },
    // Store dynamic properties for animation
    particleCount: particleCount,
    radius: radius,
    radiusVariation: radiusVariation,
    altitude: altitude,
    color: color,
    opacity: opacity,
    size: size,
    twinkleSpeed: twinkleSpeed,
    twinkleIntensity: twinkleIntensity,
    nebulaCount: nebulaCount,
    shootingStarChance: shootingStarChance,
    shape: shape,
    shapeRotation: shapeRotation,
    shapeScale: shapeScale,
    isPaused: false,
    isStopped: particle?.isStopped || false,
    // optional life in seconds; UI shows `Life Cycle` — when set, the system will be visible
    // for `life` seconds then will stop & disappear
    life: particle?.life ?? null,
    remainingLife: typeof particle?.life === 'number' ? particle.life : null,
  };

  // Set transform from particle data
  if (particle?.position) {
    starGroup.position.set(
      particle.position.x || 0,
      particle.position.y || 0,
      particle.position.z || 0
    );
  }
  if (particle?.rotation) {
    starGroup.rotation.set(
      particle.rotation.x || 0,
      particle.rotation.y || 0,
      particle.rotation.z || 0
    );
  }
  if (particle?.scale) {
    starGroup.scale.set(
      particle.scale.x || 1,
      particle.scale.y || 1,
      particle.scale.z || 1
    );
  }

  scene.add(starGroup);
  store.dispatch(setPushIntersectedObject(starGroup));
  resyncSceneParticlePoints(scene);

  // Enhanced twinkling with shooting stars
  let shootingStars = [];
  // Track previous frame time for life countdown
  let _lastFrameTime = Date.now() * 0.001;
  const animate = () => {
    if (!starGroup.parent) return;

    // Check if animation should be paused or stopped
    if (starGroup.userData?.isPaused || starGroup.userData?.isStopped) {
      starGroup.userData.animationId = requestAnimationFrame(animate);
      return;
    }

    const time = Date.now() * 0.001;
    // delta time in seconds between frames
    const dt = Math.max(0, time - _lastFrameTime);
    _lastFrameTime = time;

    // Get twinkle intensity from userData (controls how much stars dim)
    const twinkleIntensity = starGroup.userData?.twinkleIntensity ?? 0.5;
    // Map intensity (0-1) to min brightness (0.1-0.4)
    // Higher intensity = more dramatic blinking (stars dim more)
    const minBrightness = 0.4 - twinkleIntensity * 0.3; // Range: 0.4 to 0.1

    // Main star twinkling with configurable speed. Use per-particle phase & rate so
    // user-provided `twinkleSpeed` controls the overall tempo while preserving
    // per-star variation.
    const sizes = geometry.attributes.size.array;
    const phases = geometry.attributes.twinklePhase.array;
    const rates = geometry.attributes.twinkleRate.array;
    // Get base sizes from geometry userData (stored during creation)
    const baseSizes = geometry.userData.baseSizes || sizes;
    const currentBaseColors = geometry.userData.baseColors || baseColors;

    for (let i = 0; i < sizes.length; i++) {
      const phase = phases[i] || 0;
      const rate = rates[i] || twinkleSpeed;

      // Compute per-particle brightness factor from -1..1 -> 0..1 range
      // Use a more pronounced sine wave for better blinking effect
      let factor = Math.sin(time * rate + phase) * 0.5 + 0.5; // 0..1

      // Apply twinkle intensity: remap from [0,1] to [minBrightness, 1]
      // This creates a more dramatic blinking effect
      factor = minBrightness + factor * (1 - minBrightness);

      // Apply factor to base color -> update vertex color attribute for visible twinkle
      const ci = i * 3;
      geometry.attributes.color.array[ci] = currentBaseColors[ci] * factor;
      geometry.attributes.color.array[ci + 1] = currentBaseColors[ci + 1] * factor;
      geometry.attributes.color.array[ci + 2] = currentBaseColors[ci + 2] * factor;

      // Enhanced size variation for more noticeable blinking
      // Stars should pulse in size as they blink
      const sizeVariation = Math.sin(time * rate + phase) * 0.3 + 1; // 0.7 to 1.3
      const baseSize = baseSizes[i] || sizes[i];
      sizes[i] = baseSize * sizeVariation;
    }
    geometry.attributes.size.needsUpdate = true;
    geometry.attributes.color.needsUpdate = true;

    // Nebula rotation
    starGroup.children.forEach((nebula, index) => {
      // Only rotate objects explicitly marked as nebulas
      if (nebula.userData?.isNebula) {
        nebula.rotation.x += 0.001;
        nebula.rotation.y += 0.0005;
        if (nebula && nebula.material)
          nebula.material.opacity = 0.05 + Math.sin(time + index) * 0.03;
      }
    });

    // Occasional shooting stars with dynamic chance
    if (Math.random() < shootingStarChance) {
      const shootingStar = createShootingStar();
      starGroup.add(shootingStar);
      shootingStars.push(shootingStar);
    }

    // Update shooting stars
    shootingStars = shootingStars.filter((star) => {
      star.position.add(star.userData.velocity);
      if (star && star.material) star.material.opacity *= 0.98;

      if (star.material?.opacity < 0.01) {
        starGroup.remove(star);
        return false;
      }
      return true;
    });

    // handle finite life of particle system (in seconds)
    if (
      typeof starGroup.userData.remainingLife !== 'undefined' &&
      starGroup.userData.remainingLife !== null
    ) {
      // Selected in design — do not count down / expire while editing.
      if (isParticleLifecycleSuspendedInDesign(starGroup)) {
        const life =
          typeof starGroup.userData.life === 'number'
            ? starGroup.userData.life
            : null;
        if (life != null && life > 0) {
          starGroup.userData.remainingLife = life;
        }
      } else {
        // decrement remaining life
        starGroup.userData.remainingLife -= dt;

        // When life expires, stop animation and hide/remove the system
        if (starGroup.userData.remainingLife <= 0) {
          // Ensure we don't keep animating
          starGroup.userData.remainingLife = 0;
          starGroup.userData.isExpired = true;
          // stop animation loop
          console.info(
            `Particle system "${starGroup.name}" expired after lifecycle (life=${starGroup.userData.life}). hiding & stopping animation.`
          );
          stopAnimation(starGroup, starGroup.name);
          try {
            // graceful hide so things using visibility remain stable
            starGroup.visible = false;
            // also fade out material(s) quickly if available
            starGroup.traverse((c) => {
              if (c.material) {
                c.material.opacity = 0;
                c.material.transparent = true;
                c.material.needsUpdate = true;
              }
            });
          } catch (err) {
            console.warn('Failed to hide expired particle system', err);
          }

          // do not request another frame, the loop is finished for this system
          return;
        }
      }
    }

    starGroup.userData.animationId = requestAnimationFrame(animate);
  };

  const createShootingStar = () => {
    const geometry = new THREE.SphereGeometry(0.2, 8, 6);
    const material = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 1,
    });

    const star = new THREE.Mesh(geometry, material);
    star.position.set(
      (Math.random() - 0.5) * 200,
      Math.random() * 50 + 50,
      (Math.random() - 0.5) * 200
    );

    star.userData.velocity = new THREE.Vector3(
      (Math.random() - 0.5) * 2,
      -Math.random() * 1.5,
      (Math.random() - 0.5) * 2
    );

    return star;
  };

  // Store the animation function in userData for later use
  starGroup.userData.animateFunction = animate;

  // Start animation if particle is not stopped
  // If remainingLife is set and is 0, the particle system should start hidden
  const hasFiniteLife =
    typeof starGroup.userData.remainingLife === 'number' &&
    starGroup.userData.remainingLife !== null;

  if (hasFiniteLife && starGroup.userData.remainingLife <= 0) {
    // expired at creation - hide & do not start
    starGroup.visible = false;
    console.info(
      `Particle system "${starGroup.name}" created already expired (life=${starGroup.userData.remainingLife}). Hiding.`
    );
  } else if (!particle?.isStopped) {
    startAnimation(starGroup, animate, starGroup.name);
  }

  store.dispatch(selected_object(starGroup));
  selectObjectInScene(starGroup.name);
};

const updateStars = (scene, starSystem, newParticleData) => {
  if (!scene || !starSystem || starSystem.objType !== 'stars') {
    console.warn('Invalid star system or scene provided for update');
    return;
  }

  // Get current configuration
  const currentConfig = starSystem.userData || {};

  // CHECK IF PARTICLE COUNT CHANGED - RECREATE ENTIRE SYSTEM IF SO
  if (
    newParticleData?.particleCount !== undefined &&
    hasValidParticleCount(newParticleData.particleCount) &&
    newParticleData.particleCount !== currentConfig.particleCount
  ) {
    // Store current transform and animation state
    const position = starSystem.position.clone();
    const rotation = starSystem.rotation.clone();
    const scale = starSystem.scale.clone();
    const name = starSystem.name;
    const url = starSystem.url;

    const animationState = {
      isPlaying: newParticleData?.isPlaying ?? currentConfig.isPlaying ?? true,
      isStopped: newParticleData?.isStopped ?? currentConfig.isStopped ?? false,
    };

    // Remove old system
    stopAnimation(starSystem, starSystem.name);
    scene.remove(starSystem);

    // Dispose of old geometries and materials to prevent memory leaks
    starSystem.traverse((child) => {
      if (child.geometry) {
        child.geometry.dispose();
      }
      if (child.material) {
        if (child.material.map) child.material.map.dispose();
        child.material.dispose();
      }
    });

    // Create new system with updated particle count
    const mergedData = {
      ...currentConfig,
      ...newParticleData,
      id: name,
      position: { x: position.x, y: position.y, z: position.z },
      rotation: { x: rotation.x, y: rotation.y, z: rotation.z },
      scale: { x: scale.x, y: scale.y, z: scale.z },
      ...animationState,
    };

    handleStars(scene, mergedData);

    // Find and return the newly created system
    return findStarSystem(scene, name);
  }

  // Handle animation state changes
  if (
    typeof newParticleData?.isPlaying !== 'undefined' ||
    typeof newParticleData?.isStopped !== 'undefined'
  ) {
    const isPlaying =
      newParticleData?.isPlaying ?? currentConfig.isPlaying ?? true;
    const isStopped =
      newParticleData?.isStopped ?? currentConfig.isStopped ?? false;

    // Update userData flags
    starSystem.userData.isPlaying = isPlaying;
    starSystem.userData.isStopped = isStopped;
    starSystem.userData.isPaused = !isPlaying && !isStopped;

    if (isStopped) {
      stopAnimation(starSystem, starSystem.name);
    } else if (isPlaying) {
      const animateFunction = starSystem.userData.animateFunction;
      if (animateFunction) {
        startAnimation(starSystem, animateFunction, starSystem.name);
      }
    } else {
      pauseAnimation(starSystem, starSystem.name);
    }
  }

  // Merge current config with new particle data
  const updatedConfig = {
    particleCount:
      newParticleData?.particleCount || currentConfig.particleCount || 300,
    // Accept both `radius` and `range` (UI uses 'range') — treat `range` as radius so controls update the star field
    radius:
      (typeof newParticleData?.radius !== 'undefined'
        ? newParticleData.radius
        : typeof newParticleData?.range !== 'undefined'
          ? newParticleData.range
          : currentConfig.radius) || 300,
    radiusVariation:
      newParticleData?.radiusVariation || currentConfig.radiusVariation || 100,
    altitude: newParticleData?.altitude || currentConfig.altitude || 30,
    color: newParticleData?.color || currentConfig.color || '#ffffff',
    opacity: newParticleData?.opacity || currentConfig?.opacity || 0.9,
    size: newParticleData?.size || currentConfig.size || 2,
    twinkleSpeed:
      newParticleData?.twinkleSpeed || currentConfig.twinkleSpeed || 2,
    twinkleIntensity:
      newParticleData?.twinkleIntensity !== undefined
        ? newParticleData.twinkleIntensity
        : (currentConfig.twinkleIntensity ?? 0.5),
    nebulaCount: newParticleData?.nebulaCount || currentConfig.nebulaCount || 5,
    shootingStarChance:
      newParticleData?.shootingStarChance ||
      currentConfig.shootingStarChance ||
      0.002,
    shape:
      newParticleData?.shape ??
      currentConfig.shape ??
      getDefaultParticleShape('stars'),
    shapeRotation:
      newParticleData?.shapeRotation || currentConfig.shapeRotation || 0,
    shapeScale: newParticleData?.shapeScale || currentConfig.shapeScale || 1.0,
    // support life / lifecycle updates (seconds)
    life:
      typeof newParticleData?.life !== 'undefined'
        ? newParticleData.life
        : (currentConfig.life ?? null),
  };

  // Update material properties
  const starPoints = starSystem.children.find(
    (child) => child instanceof THREE.Points
  );
  if (starPoints && starPoints.material) {
    if (newParticleData?.opacity !== undefined) {
      starPoints.material.opacity = updatedConfig?.opacity;
    }
    if (newParticleData?.size !== undefined) {
      starPoints.material.size = updatedConfig.size;
    }
    // Update shape texture if shape or color changed
    if (
      newParticleData?.shape !== undefined ||
      newParticleData?.color !== undefined ||
      newParticleData?.shapeRotation !== undefined ||
      newParticleData?.shapeScale !== undefined
    ) {
      applyParticleShapeMap(
        starPoints.material,
        updatedConfig.shape,
        updatedConfig.color,
        updatedConfig.shapeRotation,
        updatedConfig.shapeScale
      );

      // Also update the vertex colors to match the new color
      if (newParticleData?.color !== undefined && starPoints.geometry && starPoints.geometry.attributes.color) {
        const newColor = new THREE.Color(updatedConfig.color);
        const colorArray = starPoints.geometry.attributes.color.array;
        const currentBaseColors = starPoints.geometry.userData.baseColors;
        
        for (let i = 0; i < colorArray.length / 3; i++) {
          if (currentBaseColors) {
            currentBaseColors[i * 3] = newColor.r;
            currentBaseColors[i * 3 + 1] = newColor.g;
            currentBaseColors[i * 3 + 2] = newColor.b;
          }
          colorArray[i * 3] = newColor.r;
          colorArray[i * 3 + 1] = newColor.g;
          colorArray[i * 3 + 2] = newColor.b;
        }
        starPoints.geometry.attributes.color.needsUpdate = true;
      }
    }
    starPoints.material.userData = starPoints.material.userData || {};
    starPoints.material.userData.authoredSize = updatedConfig.size;
    ensurePointsMaterialOrthoSupport(starPoints.material);
    starPoints.material.needsUpdate = true;
  }

  // Update transforms if provided
  if (newParticleData?.position) {
    starSystem.position.set(
      newParticleData.position.x !== undefined
        ? newParticleData.position.x
        : starSystem.position.x,
      newParticleData.position.y !== undefined
        ? newParticleData.position.y
        : starSystem.position.y,
      newParticleData.position.z !== undefined
        ? newParticleData.position.z
        : starSystem.position.z
    );
  }
  if (newParticleData?.rotation) {
    starSystem.rotation.set(
      newParticleData.rotation.x !== undefined
        ? newParticleData.rotation.x
        : starSystem.rotation.x,
      newParticleData.rotation.y !== undefined
        ? newParticleData.rotation.y
        : starSystem.rotation.y,
      newParticleData.rotation.z !== undefined
        ? newParticleData.rotation.z
        : starSystem.rotation.z
    );
  }
  if (newParticleData?.scale) {
    starSystem.scale.set(
      newParticleData.scale.x !== undefined
        ? newParticleData.scale.x
        : starSystem.scale.x,
      newParticleData.scale.y !== undefined
        ? newParticleData.scale.y
        : starSystem.scale.y,
      newParticleData.scale.z !== undefined
        ? newParticleData.scale.z
        : starSystem.scale.z
    );
  }

  // Update userData with new configuration
  starSystem.userData = {
    ...starSystem.userData,
    ...updatedConfig,
    twinkleIntensity: updatedConfig.twinkleIntensity,
    material_settings: {
      ...starSystem.userData.material_settings,
      albedo: updatedConfig.color,
    },
  };

  // If caller provided explicit life change, update remainingLife and start/stop accordingly
  if (typeof newParticleData?.life !== 'undefined') {
    const newLife = newParticleData.life;
    starSystem.userData.life = newLife;
    starSystem.userData.remainingLife =
      typeof newLife === 'number' ? newLife : null;

    // if new life is 0 (or <=0), ensure the system is stopped and hidden
    if (
      typeof starSystem.userData.remainingLife === 'number' &&
      starSystem.userData.remainingLife <= 0
    ) {
      stopAnimation(starSystem, starSystem.name);
      starSystem.visible = false;
    } else if (
      typeof starSystem.userData.remainingLife === 'number' &&
      starSystem.userData.remainingLife > 0
    ) {
      // if system was expired earlier, revive it and restart animation
      if (starSystem.userData.isExpired) starSystem.userData.isExpired = false;
      // start animation if not already running
      const animateFunction = starSystem.userData.animateFunction;
      if (animateFunction)
        startAnimation(starSystem, animateFunction, starSystem.name);
      starSystem.visible = true;
    }
  }

  // If radius or variation changed, recalculate positions so the star field updates visually
  // Recompute positions if radius (or range) or variation changed so UI updates are visible
  if (
    (typeof newParticleData?.radius !== 'undefined' &&
      newParticleData.radius !== currentConfig.radius) ||
    (typeof newParticleData?.range !== 'undefined' &&
      newParticleData.range !== currentConfig.radius) ||
    (typeof newParticleData?.radiusVariation !== 'undefined' &&
      newParticleData.radiusVariation !== currentConfig.radiusVariation)
  ) {
    try {
      const starPointsGeom = starPoints.geometry;
      if (starPointsGeom && starPointsGeom.attributes.position) {
        const positions = starPointsGeom.attributes.position.array;
        const count = positions.length / 3;

        // Recompute positions using the same spherical distribution used at creation
        for (let i = 0; i < count; i++) {
          const dynamicRadius =
            updatedConfig.radius +
          Math.random() * updatedConfig.radiusVariation;
          const theta = Math.random() * Math.PI * 2;
          const phi = Math.acos(Math.random() * 2 - 1);

          positions[i * 3] = dynamicRadius * Math.sin(phi) * Math.cos(theta);
          positions[i * 3 + 1] =
            Math.abs(dynamicRadius * Math.cos(phi)) + updatedConfig.altitude;
          positions[i * 3 + 2] =
            dynamicRadius * Math.sin(phi) * Math.sin(theta);
        }

        starPointsGeom.attributes.position.needsUpdate = true;
        starPointsGeom.computeBoundingBox();
        starPointsGeom.computeBoundingSphere();
        if (starSystem.userData) starSystem.userData.forceBoxUpdate = true;
      }
    } catch (err) {
      console.warn('Failed to update star positions on radius change', err);
    }
  }

  // If twinkleSpeed changed, update the per-particle twinkleRate attribute
  if (
    typeof newParticleData?.twinkleSpeed !== 'undefined' &&
    starPoints &&
    starPoints.geometry &&
    starPoints.geometry.attributes.twinkleRate
  ) {
    try {
      const rates = starPoints.geometry.attributes.twinkleRate.array;
      for (let i = 0; i < rates.length; i++) {
        // keep per-particle variation but re-base on new twinkleSpeed
        rates[i] = (updatedConfig.twinkleSpeed || 2) * (0.5 + Math.random());
      }
      starPoints.geometry.attributes.twinkleRate.needsUpdate = true;
    } catch (err) {
      console.warn('Failed to update twinkle rates on star system', err);
    }
  }

  // Update system metadata if ID changed
  if (newParticleData?.id && newParticleData.id !== starSystem.name) {
    starSystem.name = newParticleData.id;
    starSystem.url = newParticleData.id;
  }

  // Only dispatch update event, don't change selection
  store.dispatch(setPushIntersectedObject(starSystem));

  resyncSceneParticlePoints(scene);
  return starSystem;
};

// Helper function to find star system by name/id in scene
const findStarSystem = (scene, nameOrId) => {
  return scene.children.find(
    (child) =>
      (child.name === nameOrId || child.url === nameOrId) &&
      child.objType === 'stars'
  );
};

// Convenience function to update stars by name
const updateStarsByName = (scene, nameOrId, newParticleData) => {
  const starSystem = findStarSystem(scene, nameOrId);
  if (starSystem) {
    return updateStars(scene, starSystem, newParticleData);
  } else {
    console.warn(`Star system with name/id "${nameOrId}" not found in scene`);
    return null;
  }
};

export default handleStars;
export { updateStars, updateStarsByName };
